// Utility for document-related helpers
import { createPdfFromImages } from "./pdf-util";

/**
 * Converts a base64 string to a File object for uploads.
 * @param base64 The base64 string (no data: prefix)
 * @param filename The name for the resulting file
 * @param mimeType The MIME type for the file
 * @returns File
 */
export function base64ToFile(
  base64: string,
  filename: string,
  mimeType: string
): File {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], filename, { type: mimeType });
}

/**
 * Extracts unique years from a list of documents based on documentDate field.
 * @param documents Array of document objects with documentDate (string/Date)
 * @returns Array of years (number), sorted descending
 */
export function extractDocumentYears(
  documents: { documentDate?: string }[]
): number[] {
  const years = new Set<number>();
  documents.forEach((doc) => {
    if (doc.documentDate) {
      const d = new Date(doc.documentDate);
      if (!isNaN(d.getTime())) {
        years.add(d.getFullYear());
      }
    }
  });
  return Array.from(years).sort((a, b) => b - a);
}

/**
 * Converts a base64 string to a File object for uploads.
 * @param base64 The base64 string (no data: prefix)
 * @param filename The name for the resulting file
 * @param mimeType The MIME type for the file
 * @returns File
 */
/**
 * Handles native picker_result messages and returns processed files
 * @param event MessageEvent from native
 * @returns Promise<File[]> Array of processed files ready for upload
 */
export async function handlePickerResultMessage(
  event: MessageEvent
): Promise<File[]> {
  let data;
  try {
    data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
  } catch {
    return [];
  }

  if (data?.type !== "picker_result" || !data.payload) {
    return [];
  }

  // Handle scanner payload (creates a single PDF from multiple images)
  if (data.payload.type === "scanner" && Array.isArray(data.payload.files)) {
    try {
      const now = Date.now();
      const imageFiles = data.payload.files.map((f: any) =>
        base64ToFile(f.base64, f.name, f.mimeType)
      );
      const pdfBlob = await createPdfFromImages(imageFiles);
      const pdfFile = new File([pdfBlob], `maatra-${now}.pdf`, {
        type: "application/pdf",
      });
      return [pdfFile];
    } catch (err) {
      console.error("Failed to create PDF from scanned images:", err);
      return [];
    }
  }

  // Handle DocumentPicker multi-file or single-file payloads (image or file)
  if (
    (data.payload.type === "image" || data.payload.type === "file") &&
    data.payload.files
  ) {
    const files = Array.isArray(data.payload.files)
      ? data.payload.files
      : [data.payload.files];
    return files.map((file: any) => {
      if (file.base64) {
        return base64ToFile(file.base64, file.name, file.mimeType);
      }
      return file;
    });
  }

  // Legacy fallback (single or array)
  const payload = Array.isArray(data.payload) ? data.payload : [data.payload];
  return payload.map((file: any) => {
    if (file.base64) {
      return base64ToFile(file.base64, file.name, file.mimeType);
    }
    return file;
  });
}
